"use client";

import { useId, useRef, useState, type KeyboardEvent } from "react";
import type { CFaqContent, CFaqItem } from "@/lib/api/cms-catalog";
import { Input } from "@/components/ui/Form";
import { Icon } from "@/lib/icons/Icon";
import { useT } from "@/lib/i18n/context";
import { cFaqTranslations, type CFaqTranslations } from "./CFaq.i18n";
import styles from "./CFaq.module.css";

/**
 * CFaq - visitor view komponentu `c_faq` (Najczęściej zadawane pytania).
 *
 * Migracja z legacy Smarty templatu `c_faq_default_modern.imp` + jQuery
 * `slideToggle` na czystego Reacta - pojedynczy panel sterowany `useState`
 * (multi-open: każde pytanie ma niezależny stan rozwinięcia).
 *
 * **Data source:** `C_FAQ_CONTROLLER::getApiContent()` zwraca:
 *   ```
 *   { id, symbol, subtitle, items: [{ id, question, answer }, ...] }
 *   ```
 *
 * **A11y (WAI-ARIA APG "Accordion"):**
 *  - Pytanie = natywny `<button>` w `<h2>` (pod `<h1>` tytułu katalogu
 *    z CDefault - SR nawiguje między sekcjami listą nagłówków; poziom
 *    nagłówka MUSI schodzić sekwencyjnie z h1, inaczej Lighthouse a11y
 *    zgłasza "Heading elements not in sequentially-descending order").
 *  - Panel odpowiedzi ma `role="region"` + `aria-labelledby` wskazujący
 *    button (region bez nazwy = naruszenie SC 4.1.2).
 *  - Klawiatura: Enter/Spacja toggluje panel (natywne dla `<button>`),
 *    ArrowDown/ArrowUp/Home/End przesuwają focus między pytaniami
 *    (roving focus wg WAI-ARIA APG).
 *
 * **Security:** `item.answer` to HTML z admin panel (IHTMLEditor w
 * `c_faq_admin.php`) - trusted source, OK dla `dangerouslySetInnerHTML`.
 */
export function CFaq({ content }: CFaqProps) {
  const t = useT(cFaqTranslations);
  const [query, setQuery] = useState("");
  const searchId = useId();
  const listId = useId();
  const headerRefs = useRef<Array<HTMLButtonElement | null>>([]);

  if (!content || typeof content !== "object") {
    return <div className={styles.empty}>{t.emptyData}</div>;
  }

  const items: CFaqItem[] = Array.isArray(content.items) ? content.items : [];
  const subtitle = content.subtitle?.trim() ?? "";

  const normalizedQuery = query.trim().toLowerCase();
  const filtered = normalizedQuery
    ? items.filter((item) => buildHaystack(item).includes(normalizedQuery))
    : items;

  const focusHeader = (index: number) => {
    const total = headerRefs.current.length;
    if (total === 0) return;
    const next = ((index % total) + total) % total;
    headerRefs.current[next]?.focus();
  };

  const handleHeaderKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        focusHeader(index + 1);
        break;
      case "ArrowUp":
        event.preventDefault();
        focusHeader(index - 1);
        break;
      case "Home":
        event.preventDefault();
        focusHeader(0);
        break;
      case "End":
        event.preventDefault();
        focusHeader(headerRefs.current.length - 1);
        break;
    }
  };

  return (
    <div className={styles.root}>
      {subtitle && (
        <div className={styles.subtitle}>
          <span>{subtitle}</span>
        </div>
      )}

      {items.length > 0 && (
        <div role="search">
          <Input
            id={searchId}
            type="search"
            aria-label={t.searchLabel}
            aria-controls={filtered.length > 0 ? listId : undefined}
            placeholder={t.searchPlaceholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoComplete="off"
            spellCheck={false}
            prefix={<Icon name="magnifying-glass" />}
          />
        </div>
      )}

      {items.length === 0 ? (
        <div className={styles.empty}>{t.emptyList}</div>
      ) : filtered.length === 0 ? (
        <div className={styles.empty} role="status" aria-live="polite">
          {t.formatEmptyResults(query.trim())}
        </div>
      ) : (
        <ul id={listId} className={styles.list}>
          {filtered.map((item, index) => (
            <FaqRow
              key={item.id}
              item={item}
              index={index + 1}
              t={t}
              buttonRef={(el) => {
                headerRefs.current[index] = el;
              }}
              onHeaderKeyDown={(event) => handleHeaderKeyDown(event, index)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Frontend haystack do wyszukiwarki: pytanie + tekst odpowiedzi z odartymi
 * tagami HTML, wszystko lowercase. `item.answer` pochodzi z IHTMLEditor
 * (trusted admin), więc prosty regex do stripowania wystarczy - użytkownik
 * i tak wpisuje plain text, nie znaczniki.
 */
function buildHaystack(item: CFaqItem): string {
  const answerText = item.answer.replace(/<[^>]*>/g, " ");
  return `${item.question} ${answerText}`.toLowerCase();
}

export type CFaqProps = {
  content: CFaqContent;
};

function FaqRow({
  item,
  index,
  t,
  buttonRef,
  onHeaderKeyDown,
}: {
  item: CFaqItem;
  index: number;
  t: CFaqTranslations;
  buttonRef: (el: HTMLButtonElement | null) => void;
  onHeaderKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => void;
}) {
  const [open, setOpen] = useState(false);
  const buttonId = useId();
  const panelId = useId();

  return (
    <li className={`${styles.row} ${open ? styles.rowActive : ""}`}>
      <h2 className={styles.header}>
        <button
          ref={buttonRef}
          id={buttonId}
          type="button"
          className={styles.question}
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen((v) => !v)}
          onKeyDown={onHeaderKeyDown}
        >
          <span className={styles.questionLabel}>
            {t.formatQuestion(index, item.question)}
          </span>
          <span className={styles.chevron} aria-hidden="true">
            <ChevronDown />
          </span>
        </button>
      </h2>
      <div
        id={panelId}
        role="region"
        aria-labelledby={buttonId}
        hidden={!open}
        className={styles.answer}
        dangerouslySetInnerHTML={{ __html: item.answer }}
      />
    </li>
  );
}

function ChevronDown() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="15"
      height="15"
      fill="currentColor"
      focusable="false"
      aria-hidden="true"
    >
      <path d="M12 16.5 3.75 8.25l1.5-1.5L12 13.5l6.75-6.75 1.5 1.5z" />
    </svg>
  );
}
